import {
  booleanAttribute,
  computed,
  DestroyRef,
  Directive,
  inject,
  input,
} from '@angular/core';
import { PlaybackService } from '@splixer/player';
import { AnyHash } from '@hidat/huijs-common';
import { Router } from '@angular/router';
import {
  Pv2ImageShape,
  Pv2ImageSize,
  Pv2PlayButtonPosition,
} from '../../content';
import { ContentMenuItem } from '../../models';

@Directive()
export class V3PlayableContentItemController {
  public item = input.required<ContentMenuItem>();
  public eventSource = input.required<string>();
  public playButtonPosition = input<Pv2PlayButtonPosition>('right');
  public imageSize = input<Pv2ImageSize>('large');
  public imageShape = input<Pv2ImageShape>('square');
  public shrinkOnMobile = input(true, { transform: booleanAttribute });
  public displayDuration = input(false, { transform: booleanAttribute });
  public displayThirdLine = input(true, { transform: booleanAttribute });
  public compactListItem = input(false, { transform: booleanAttribute });

  protected router = inject(Router);
  protected playbackService = inject(PlaybackService);

  protected destroyRef = inject(DestroyRef);

  /**
   * Aria Label for the tile
   * @protected
   */
  protected ariaLabelText = computed(() => {
    const item = this.item();
    let label: string;
    if (item.ariaLabel !== null) {
      label = item.ariaLabel ?? item.title ?? item.name ?? '';
    } else {
      label = '';
    }
    return label;
  })

  protected itemKlasses = computed(() => {
    const item = this.item();
    const klasses: string[] = [];
    if (item.styleClass) {
      klasses.push(item.styleClass);
    }

    const imageSize = this.imageSize();
    if (imageSize === 'small' || this.compactListItem()) {
      klasses.push('compact-list-item');
    }
    if (
      item?.playable &&
      item.contentId === this.playbackService.currentMix()?.uid
    ) {
      klasses.push('playing-mix');
    }
    return klasses;
  });

  protected imageKlasses = computed(() => {
    const klasses: string[] = [];
    const imageSize = this.imageSize();
    if (imageSize !== 'none') {
      const imageShape = this.imageShape();
      if (imageShape !== 'auto') {
        klasses.push('shape-' + imageShape);
      }
      klasses.push('size-' + imageSize);

      if (this.playButtonPosition() === 'on-image') {
        klasses.push('has-play-button');
      }
    }
    return klasses;
  });

  protected imageStyles = computed(() => {
    let styles: AnyHash | undefined;
    const imageSize = this.imageSize();
    const item = this.item();
    if (item && imageSize !== 'none') {
      if (item.backgroundImage) {
        styles = {
          'background-image': "url('" + item.backgroundImage + "')",
          'background-position': 'center',
          'background-repeat': 'no-repeat',
          'background-size': 'cover',
        };
      }
    }
    return styles;
  });

  protected clickEvent = (event: Event) => {
    if (event) {
      event.stopPropagation();
    }
    this.itemClicked();
  };

  protected itemClicked() {
    const item = this.item();
    if (item) {
      console.debug('button clicked');
      if (item.routerLink) {
        let link: string[];
        if (typeof item.routerLink === 'string') {
          link = [item.routerLink];
        } else {
          link = item.routerLink;
        }
        this.router.navigate(link);
      }
    }
  }

  protected playEvent = (event?: Event) => {
    if (event) {
      event.stopPropagation();
    }
    const item = this.item();
    if (item && item.playable && item.contentId) {
      this.playItem(item);
    }
  };

  protected playItem(item: ContentMenuItem) {
    if (
      item.playable &&
      item.contentId === this.playbackService.currentMix()?.uid
    ) {
      this.playbackService.togglePlayback(this.eventSource());
    } else if (item.contentId) {
      this.playbackService
        .playMix(item.contentId, 'content_list')
        .subscribe((res) => {
          if (res) {
            this.playbackStarted(item);
          }
        });
    }
  }

  protected playbackStarted(item: ContentMenuItem) {}

  protected isPlaying = computed(() => {
    const item = this.item();
    if (
      item?.playable &&
      item.contentId === this.playbackService.currentMix()?.uid
    ) {
      return !this.playbackService.isPlaybackPaused();
    }
    return false;
  });
}
